/**
 * The two directions between a rich-text field's HTML and plain text.
 *
 * Lives next to RichTextArea because it is that component's other half: the
 * editor produces markup, and every destination that is NOT an HTML sink needs
 * the words back out of it.
 *
 * WHY A PARSER, not a regex strip:
 *  - `parseFromString` is case- and whitespace-insensitive by construction, so
 *    `<B>` and `< b >` can't slip past the way they do a hand-rolled pattern;
 *  - it decodes entities for free (`&nbsp;`, `&amp;`), which matters when the
 *    caller later upper-cases the result — `&AMP;` is not a valid reference;
 *  - the parsed document has NO browsing context, so nothing in it executes and
 *    no `<img src=x onerror=…>` fires. Assigning to a detached element's
 *    `innerHTML` would start those loads. Paste-from-Word is the realistic way
 *    foreign markup arrives, so this is not hypothetical.
 */

/** Elements whose end is a line break, not just a styling boundary. */
const BLOCK_SELECTOR =
  "p,div,li,tr,h1,h2,h3,h4,h5,h6,blockquote,section,article,pre";

/**
 * Entity names are CASE-SENSITIVE, so text that was escaped and later
 * upper-cased carries dead references: `&nbsp;` becomes `&NBSP;`, which no
 * parser decodes and which therefore prints as those six literal characters.
 *
 * Repaired rather than passed through because the caller may WRITE the result
 * back (resuming and re-saving a draft) — faithfully propagating text we know
 * to be corrupt is worse than healing it. Only these ASCII names are listed:
 * blanket-lowercasing would turn `&Aacute;` (Á) into `&aacute;` (á).
 */
const UPPERCASED_ENTITIES = /&(NBSP|AMP|LT|GT|QUOT|APOS);/g;

/**
 * Rich text -> the lines a reader would see, trimmed, blanks dropped.
 *
 * IDEMPOTENT: plain text in gives the same plain text back, which is what makes
 * it safe to call defensively at more than one boundary (unlike escaping, which
 * must happen exactly once).
 */
export const htmlToLines = (html) => {
  const raw = String(html == null ? "" : html);
  if (!raw.trim()) return [];

  const doc = new DOMParser().parseFromString(
    raw.replace(UPPERCASED_ENTITIES, (m, name) => `&${name.toLowerCase()};`),
    "text/html"
  );
  const body = doc.body;
  if (!body) return [];

  // A <br> IS the line break…
  body.querySelectorAll("br").forEach((br) => {
    if (br.parentNode) br.parentNode.replaceChild(doc.createTextNode("\n"), br);
  });
  // …and so is either edge of a block, which is how a contentEditable records
  // "the user pressed Enter" and how list items separate.
  body.querySelectorAll(BLOCK_SELECTOR).forEach((el) => {
    el.appendChild(doc.createTextNode("\n"));
    if (el.parentNode) el.parentNode.insertBefore(doc.createTextNode("\n"), el);
  });

  return (body.textContent || "")
    .split("\n")
    // \s covers the non-breaking spaces a paste brings with it.
    .map((line) => line.replace(/\s+/g, " ").trim())
    .filter(Boolean);
};

/** Rich text -> one plain line. Line structure collapses to spaces. */
export const htmlToText = (html) => htmlToLines(html).join(" ");

/**
 * The inverse: plain text -> markup safe to hand a rich-text field as its seed.
 *
 * Escape, do NOT sanitise — the input here is text, so a typed "<b>" must show
 * as the characters `<b>` rather than quietly becoming formatting. (Sanitising
 * belongs at the other boundary, where wire HTML is rendered.)
 */
export const textToHtml = (text) =>
  String(text == null ? "" : text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
    .replace(/\n/g, "<br>");

/**
 * The tags a RichTextArea toolbar can actually produce, plus the ones a browser
 * substitutes for them. `execCommand("bold")` emits <b> on some engines and
 * <strong> on others; the same is true of i/em and u/ins, so all of them are
 * listed rather than assuming one.
 *
 * `a` is here because a link is a thing you can write into a message to a
 * patient. Every other attribute is dropped — see below.
 *
 * SPAN is deliberately ABSENT. RichTextArea turns styleWithCSS off, so its
 * buttons emit real elements rather than styled spans; a span reaching here
 * came from a paste, and once its style attribute is stripped it carries
 * nothing at all. Unwrapping it keeps the words and loses the noise. DIV stays
 * because contentEditable uses it structurally, for line breaks.
 */
const RICH_TAGS = new Set([
  "B", "STRONG", "I", "EM", "U", "INS", "S", "STRIKE", "DEL",
  "UL", "OL", "LI", "BR", "P", "DIV", "A",
]);

/**
 * OPT-IN extras, for a field whose toolbar offers a block style or a text size
 * (see RichTextArea's `styleControls`). Off by default: every other consumer of
 * this sanitiser - progress notes, prescription instructions - keeps exactly
 * the markup it accepted before, so widening one field cannot widen theirs.
 *
 *  headings : H1-H3, what execCommand("formatBlock") emits.
 *  fontSize : SPAN, carrying ONLY a `font-size` in px. Nothing else on a span
 *             survives, so the sender's colours and fonts still cannot ride in.
 */
const HEADING_TAGS = ["H1", "H2", "H3"];
const FONT_SIZE_PX = /(?:^|;)\s*font-size\s*:\s*(\d{1,2}(?:\.\d+)?)px\s*(?:;|$)/i;

/** The only attribute that survives, and only on a link. */
const SAFE_HREF = /^(https?:|mailto:|tel:)/i;

/**
 * Rich text -> rich text, keeping the formatting a person applied and nothing
 * else.
 *
 * WHY THIS EXISTS RATHER THAN FLATTENING. A contentEditable hands back whatever
 * the DOM holds, which after a paste from Word or Outlook is a thicket of
 * styles, classes, `<o:p>` and font tags. The previous approach flattened the
 * lot to escaped text — safe, but it also threw away the bold and the bullets
 * the operator had just applied, which made every button on the toolbar
 * decorative. This keeps the structure and drops the payload.
 *
 * WHAT IT REMOVES:
 *  - every element not in RICH_TAGS, UNWRAPPED rather than deleted, so the
 *    words inside a rejected <font> survive;
 *  - every attribute, including `style`, `class`, `id` and all `on*` handlers.
 *    A surviving `style` is how pasted markup smuggles in colours and fonts
 *    that then render as the sender's, and `on*` is the obvious one;
 *  - `href`s that are not http(s)/mailto/tel, which is what stops
 *    `javascript:` surviving into a sink that renders this.
 *
 * Parsed, never regexed, for the reasons in this file's header — and the parsed
 * document has no browsing context, so nothing in it loads or executes on the
 * way through.
 */
export const sanitizeRichText = (html, options) => {
  const raw = String(html == null ? "" : html);
  if (!raw.trim()) return "";

  const doc = new DOMParser().parseFromString(
    raw.replace(UPPERCASED_ENTITIES, (m, name) => `&${name.toLowerCase()};`),
    "text/html"
  );
  const body = doc.body;
  if (!body) return "";

  const opts = options || {};
  const allowed = new Set(RICH_TAGS);
  if (opts.headings) HEADING_TAGS.forEach((tag) => allowed.add(tag));
  if (opts.fontSize) allowed.add("SPAN");

  /* Depth-first over a STATIC list: unwrapping mutates the tree, and a live
     NodeList would skip siblings as they shift. */
  const walk = (node) => {
    Array.from(node.children).forEach(walk);

    /* A span's ONLY survivor, and only when asked for: a font size in px, in a
       sane range (0px hides the words, 400px is not a message). Probed BEFORE
       the allow test so a span carrying anything else - or nothing usable -
       unwraps like any other rejected tag instead of lingering as an empty
       shell around the words. */
    const px =
      opts.fontSize && node.tagName === "SPAN"
        ? Number((FONT_SIZE_PX.exec(node.getAttribute("style") || "") || [])[1])
        : NaN;
    const keepSize = px >= 8 && px <= 48;

    if (!allowed.has(node.tagName) || (node.tagName === "SPAN" && !keepSize)) {
      /* Unwrap: move the children up, then drop the shell. The text a person
         typed is never collateral. */
      const parent = node.parentNode;
      if (!parent) return;
      while (node.firstChild) parent.insertBefore(node.firstChild, node);
      parent.removeChild(node);
      return;
    }

    const href = node.tagName === "A" ? node.getAttribute("href") : null;
    /* Every attribute goes, then the one survivor is put back. Iterating a copy
       because removing mutates the live NamedNodeMap. */
    Array.from(node.attributes).forEach((attr) => node.removeAttribute(attr.name));
    /* Re-serialised from the matched NUMBER, never the original string, so a
       declaration list cannot ride in behind a valid font-size. */
    if (keepSize) node.setAttribute("style", `font-size:${px}px`);
    if (href && SAFE_HREF.test(href.trim())) {
      node.setAttribute("href", href.trim());
      /* A link in a message opens away from wherever it is rendered. */
      node.setAttribute("rel", "noopener noreferrer");
      node.setAttribute("target", "_blank");
    }
  };
  Array.from(body.children).forEach(walk);

  return body.innerHTML;
};
